import * as React from 'react';
import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { camelToKebab } from '../../../Scripts/Spotlight/Components/CamelToKebab';
import { COLOR } from '../../packages/constants';
import Tabs, { TabContent, TabListContainer, TabList, TabListItem, TabTitle } from '../tabs';
import { ColorPickerPalette } from './atoms/color-picker-palette/color-picker-palette';
import { SpotlightPicker } from './atoms/custom-color-picker';
import { Color } from './color-picker-config';
import defaultPalettes from './color-picker-config.json';

export interface ColorPickerProps {
    color: string;
    currentPalette?: string;
    palettes?: {
        [paletteName: string]: {
            [colorName: string]: Color;
        };
    };
    dataId?: string;
    onColorChange: (color: any) => void;
}

export interface ExtendedColorPickerProps extends ColorPickerProps {
    selectedTab?: number;
    ref: any;
    onTabChange?: (newIndex: number) => void;
}

const defaultTabData = [
    {
        id: 'tab-1',
        title: 'Standard',
        paletteName: 'Standard',
        content: 'Standard color palette',
    },
    {
        id: 'tab-2',
        title: 'Deep',
        paletteName: 'Deep',
        content: 'Deep color palette',
    },
    {
        id: 'tab-3',
        title: 'Custom',
        paletteName: '',
        content: 'Custom color picker',
    },
];

const StyledTabListContainer = styled(TabListContainer)`
    margin: -2px;
`;

const StyledTabList = styled(TabList)`
    max-width: unset;
`;

const StyledListItem = styled(TabListItem)`
    &:not(:first-child) {
        margin-left: -2px;
    }

    &:focus-within {
        z-index: 1000;
    }
`;

const StyledTabTitle = styled(TabTitle)`
    border-width: 2px;
`;

const StyledTabContent = styled(TabContent)`
    border: none;
    border-top: 2px solid ${COLOR.MAIN.MEDIUM_GREY};
    border-radius: unset;
    padding: 0;
`;

const StyledColorPicker = styled.div`
    width: 390px;
    background-color: ${COLOR.PRIMARY.WHITE};
    border: 2px solid ${COLOR.MAIN.MEDIUM_GREY};
    border-radius: 4px;
`;

export const ColorPicker = React.forwardRef<HTMLDivElement, ExtendedColorPickerProps>(
    (
        {
            color,
            palettes,
            currentPalette,
            selectedTab = 0,
            dataId = 'color-picker',
            onColorChange,
            onTabChange,
            ...props
        },
        ref,
    ) => {
        const [tabData, setTabData] = useState(defaultTabData);
        const [tabIndex, setTabIndex] = useState(
            // Default to last tab if selected tab exceeds number of tabs
            (selectedTab ?? tabData.length - 1) < tabData.length ? selectedTab : tabData.length - 1,
        );
        const [currentColor, setCurrentColor] = useState(color);

        const colorPalettes = palettes ?? defaultPalettes.palettes;

        const handleTabChange = (newTabIndex: number) => {
            setTabIndex(newTabIndex);
            onTabChange && onTabChange(newTabIndex);
        };

        const handleColorChange = (color: any) => {
            const hex = color.hex ? color.hex : color;

            setCurrentColor(hex);
            onColorChange(hex);
        };

        useEffect(() => {
            const prependCurrentPaletteTab = () => {
                const currentPaletteTab = {
                    id: 'tab-0',
                    title: 'Current Palette',
                    paletteName: currentPalette ?? '',
                    content: 'Current color palette',
                };

                const isNotStandardPalette = currentPalette && currentPalette !== 'Standard';
                const hasNoCurrentPaletteTab = !tabData.map((s) => s.id).includes(currentPaletteTab.id);
                const canPrependTab =
                    isNotStandardPalette &&
                    hasNoCurrentPaletteTab &&
                    Object.keys(colorPalettes).includes(currentPalette);
                if (canPrependTab) {
                    setTabData([currentPaletteTab, ...defaultTabData]);
                    setTabIndex(selectedTab ?? 0);
                }
            };

            prependCurrentPaletteTab();
        }, [currentPalette, colorPalettes, selectedTab, tabData]);

        useEffect(() => {
            // Default to last tab if selected tab exceeds number of tabs
            setTabIndex((selectedTab ?? tabData.length - 1) < tabData.length ? selectedTab : tabData.length - 1);
        }, [selectedTab, tabData]);

        return (
            <StyledColorPicker {...props} ref={ref} data-test-id={`${dataId}`}>
                <Tabs>
                    <StyledTabListContainer>
                        <StyledTabList>
                            {tabData.map((tab: any, index: number) => (
                                <StyledListItem key={`tab-${tab.id}`}>
                                    <StyledTabTitle
                                        firstTab={index === 0}
                                        lastTab={index === tabData.length - 1}
                                        dataId={`${dataId}__tab-btn-${camelToKebab(tab.title.toLowerCase())}`}
                                        selected={tabIndex === index}
                                        onClick={() => handleTabChange(index)}
                                        title={tab.title}
                                    >
                                        {tab.title || 'tab'}
                                    </StyledTabTitle>
                                </StyledListItem>
                            ))}
                        </StyledTabList>
                    </StyledTabListContainer>
                    {tabData.map((tab: any, index: number) => (
                        <StyledTabContent selected={tabIndex === index} key={`content-${tab.id}`}>
                            {tab.title === 'Custom' ? (
                                <SpotlightPicker
                                    color={currentColor}
                                    dataId={`${dataId}-custom`}
                                    onChange={handleColorChange}
                                />
                            ) : (
                                <ColorPickerPalette
                                    color={currentColor}
                                    palette={colorPalettes[tab.paletteName]}
                                    dataId={`${dataId}-palette`}
                                    onColorSelect={handleColorChange}
                                />
                            )}
                        </StyledTabContent>
                    ))}
                </Tabs>
            </StyledColorPicker>
        );
    },
);

ColorPicker.displayName = 'ColorPicker';
